import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('touchstart', this.#handleTouchStart, { signal, passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd, { signal, passive: false });
    this.addEventListener('touchcancel', this.#resetTouch, { signal, passive: true });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };
  #touchStartX = null;

  #touchStartY = null;

  #touchStartTime = 0;

  #resetTouch = () => {
    this.#touchStartX = null;
    this.#touchStartY = null;
    this.#touchStartTime = 0;
  };

  #handleTouchStart = (event) => {
    if (!window.matchMedia('(max-width: 749px)').matches || event.touches.length !== 1) {
      this.#resetTouch();
      return;
    }

    if (!(event.target instanceof Element)) return;
    if (event.target.closest('slideshow-arrows, slideshow-controls, video, model-viewer, input, select, textarea')) {
      this.#resetTouch();
      return;
    }

    const touch = event.touches[0];
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
    this.#touchStartTime = performance.now();
  };

  #handleTouchEnd = (event) => {
    if (this.#touchStartX === null || this.#touchStartY === null || event.changedTouches.length !== 1) {
      this.#resetTouch();
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.#touchStartX;
    const deltaY = touch.clientY - this.#touchStartY;
    const elapsed = performance.now() - this.#touchStartTime;
    this.#resetTouch();

    if (elapsed > 1000 || Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

    event.preventDefault();
    if (deltaX < 0) {
      this.slideshow?.next(event, { animate: true });
    } else {
      this.slideshow?.previous(event, { animate: true });
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
